import React, { useState } from "react";
import { FaGithub } from "react-icons/fa";
import { FaTelegram, FaXTwitter, FaYoutube } from "react-icons/fa6";
import { SiFarcaster } from "react-icons/si";
import blokc from "@/public/blokc.svg";
import Image from "next/image";
import { cn } from "@/lib/utils";
import { motion } from "framer-motion";
const socialLinks = [
  {
    icon: blokc,
    isImage: true,
    title: "How to contribute",
    desc: "Find out all the different ways you can contribute to BLOK Capital.",
    href: "https://docs.blokcapital.io/category/contribute-with-us",
    color: "text-emerald-600",
  },
  {
    icon: FaGithub,
    title: "GitHub",
    desc: "Contribute to code, design, articles, etc.",
    href: "https://github.com/BLOKCapital",
    color: "text-gray-800",
  },
  {
    icon: FaTelegram,
    title: "Telegram",
    desc: "Chat with us—instant support from Telegram mods and a global community.",
    href: "https://t.me/blok_capital",
    color: "text-sky-500",
  },
  {
    icon: FaXTwitter,
    title: "X",
    desc: "To keep up with our updates and important news.",
    href: "https://x.com/blok_cap",
    color: "text-black",
  },
  {
    icon: SiFarcaster,
    title: "Farcaster",
    desc: "Join us in the decentralized social future.",
    href: "https://warpcast.com/blokc",
    color: "text-indigo-500",
  },
  {
    icon: FaYoutube,
    title: "YouTube",
    desc: "Watch our latest videos and tutorials.",
    href: "https://www.youtube.com/@blokcapital",
    color: "text-red-600", // YouTube red
  },
];

export function CommunitySection() {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  return (
    <div className="my-16 sm:my-24 px-4 sm:px-6 lg:px-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="text-center mb-10 sm:mb-14"
      >
        <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-black mb-4">
          Join Our Community
        </h2>
        <p className="text-base sm:text-lg text-gray-700 max-w-2xl mx-auto px-2 sm:px-4 mb-8 sm:mb-12">
          Stay connected with us, get support, and be part of the BLOK Capital
          revolution.
        </p>
      </motion.div>

      <section
        className={cn(
          "relative z-10 max-w-[1380px] w-full mx-auto p-4 sm:p-8 lg:p-10 rounded-t-3xl backdrop-blur-xl",
          "bg-gradient-to-b from-[#7DD2FD] from-1% via-white via-40% to-white to-100%",
          "after:content-[''] after:absolute after:bottom-0 after:left-0 after:right-0 after:h-16 after:rounded-b-[2rem]",
          "after:bg-gradient-to-b after:from-transparent after:to-white after:z-20",
          "shadow-none border border-white/10"
        )}
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mt-6 sm:mt-10">
          {socialLinks.map((item, idx) => {
            const isActive = activeIndex === idx;

            return (
              <a
                key={idx}
                href={item.href}
                target="_blank"
                rel="noopener noreferrer"
              >
                <div
                  className={cn(
                    "group w-full flex items-start gap-4 sm:gap-6 p-4 sm:p-6 rounded-2xl transition-all duration-300",
                    isActive
                      ? `border-2 border-current ${item.color}`
                      : "border-2 border-transparent"
                  )}
                >
                  {/* ICON BOX */}
                  <div
                    className={cn(
                      "bg-white rounded-xl p-3 sm:p-4 shadow transition-transform duration-300",
                      "group-hover:scale-110"
                    )}
                  >
                    {item.isImage ? (
                      <div className="w-8 sm:w-10 h-8 sm:h-10">
                        <Image
                          src={item.icon}
                          alt={item.title}
                          width={40}
                          height={40}
                          className="w-full h-full object-contain"
                        />
                      </div>
                    ) : (
                      <item.icon
                        className={cn("w-8 h-8 sm:w-10 sm:h-10", item.color)}
                      />
                    )}
                  </div>

                  {/* TEXT BLOCK */}
                  <div>
                    <h3
                      className={cn(
                        "font-semibold text-lg sm:text-xl",
                        item.color
                      )}
                    >
                      {item.title}
                    </h3>
                    <p className="text-sm sm:text-base text-gray-600">
                      {item.desc}
                    </p>
                  </div>
                </div>
              </a>
            );
          })}
        </div>
      </section>
    </div>
  );
}
